import * as CertificateBatchConstants from "../constants/CertificateBatchConstants";
import { toSavedFilters } from "./SavedReportFilters";

// The completion date range is chosen as local dates; the API compares UTC
// instants, so the range runs from the start of the first day to the last
// millisecond of the last day in the browser's time zone.
export const startOfLocalDay = (date) =>
  date
    ? new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0,
        0,
        0,
        0
      ).toISOString()
    : null;

export const endOfLocalDay = (date) =>
  date
    ? new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59,
        999
      ).toISOString()
    : null;

// "yyyy-MM-dd": the calendar day picked in a date field (a Date at the start
// of a local day). A certificate prints its completion day in the
// enrolment's time zone (UTC when it has none), so "Completed between" must
// be about those days, not the admin's browser days: the chosen days are
// sent as they are, for the API to compare with each completion's day in its
// enrolment's time zone (C-BUG-3, tracker F29).
export const chosenDay = (date) =>
  date
    ? [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-")
    : null;

export const trainingTypeNumber = (type) =>
  CertificateBatchConstants.CERTIFICATE_BATCH_TRAINING_TYPES[type] ??
  CertificateBatchConstants.CERTIFICATE_BATCH_TRAINING_TYPES.required;

// Body of POST api/certificates/batches/preview and POST api/certificates/batches:
// the chosen items (rows of the items list, { itemType, id }) or All training,
// and the learners' filters. userFilters is the report screens' UserFilters
// state; values-less filters are left out and Designation, Department and
// ManagerId are built-in fields, the same rule the report requests use.
// audiences are the chosen rows of the audience list: only their ids are sent
// (none: every learner).
export const toCertificateBatchRequest = ({
  items = [],
  allTraining = false,
  completedFrom = null,
  completedTo = null,
  userFilters = [],
  audiences = [],
  trainingType,
}) => ({
  items: allTraining
    ? []
    : items.map((item) => ({ itemType: item.itemType, itemId: item.id })),
  allTraining,
  filters: {
    completedFrom: startOfLocalDay(completedFrom),
    completedTo: endOfLocalDay(completedTo),
    // The chosen days themselves (see chosenDay); the instants above are the
    // browser's days, which the API reads until it compares the days.
    completedFromDay: chosenDay(completedFrom),
    completedToDay: chosenDay(completedTo),
    userFilters: toSavedFilters({ userFilters }).userFilters,
    audienceIds: audiences.map((audience) => audience.id),
    trainingType: trainingTypeNumber(trainingType),
  },
});

// The dialog's options: every item with a key that is unique across both item
// types and the label the list shows ("Fire safety (Learning Program)").
export const toItemOptions = (items = [], labelOf) =>
  items.map((item) => ({
    ...item,
    key: `${item.itemType}:${item.id}`,
    label: labelOf(item),
  }));

// All training or more than one item: the ZIP has one folder per learner.
export const isMultiItem = (allTraining, items = []) =>
  Boolean(allTraining) || items.length > 1;

// "Department: Sales, Support" for each user filter with values; option ids
// are shown by their names when the filter has loaded its options.
export const describeUserFilters = (userFilters = []) =>
  userFilters
    .filter((filter) => filter.values?.length > 0)
    .map((filter) => {
      const values = filter.values.map(
        (value) =>
          (filter.options || []).find((option) => option.id === value)?.name ||
          value
      );
      return `${filter.name || filter.id}: ${values.join(", ")}`;
    });

export const isTooMany = (count) =>
  count > CertificateBatchConstants.CERTIFICATE_BATCH_LIMIT;

// Prepare download is offered only for a preview with a certificate and 1 to
// 2,000 certificates.
export const canStart = (preview) =>
  Boolean(
    preview?.hasCertificate && preview.count > 0 && !isTooMany(preview.count)
  );

// A Ready batch whose file is gone (expired) shows as Expired.
export const statusLabelId = (batch) =>
  batch.status === CertificateBatchConstants.CERTIFICATE_BATCH_STATUS_READY &&
  !batch.canDownload
    ? "certificate_batch_status_expired"
    : CertificateBatchConstants.CERTIFICATE_BATCH_STATUS_LABELS[batch.status];

export const statusSeverity = (batch) =>
  batch.status === CertificateBatchConstants.CERTIFICATE_BATCH_STATUS_READY &&
  !batch.canDownload
    ? "secondary"
    : CertificateBatchConstants.CERTIFICATE_BATCH_STATUS_SEVERITIES[
        batch.status
      ];

export const isPending = (batch) =>
  batch.status === CertificateBatchConstants.CERTIFICATE_BATCH_STATUS_QUEUED ||
  batch.status ===
    CertificateBatchConstants.CERTIFICATE_BATCH_STATUS_IN_PROGRESS;

// The API names the ZIP in Content-Disposition (exposed through CORS);
// without a readable header the file is certificates.zip.
export const batchFileName = (headers = {}) => {
  const disposition = headers["content-disposition"] || "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    return decodeURIComponent(encoded[1].trim());
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1].trim() : "certificates.zip";
};
